import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { reviewAPI, createQueryKey } from '../../services/api';
import Stars from './Stars';

export default function ReviewModal({ projectId, project, currentUser, isOpen, onClose, existingReview, initialVisitedAt, sourceProjectId, chatId = null, sessionTitle = null }) {
  const qc = useQueryClient();
  const tagsQuery = useQuery({ queryKey: ['review-tags'], queryFn: () => reviewAPI.getTags(), select: (r) => r.data.data.tags });

  const todayStr = new Date().toISOString().slice(0,10);
  const [form, setForm] = useState({ rating: 0, tags: [], comment: '', difficulty: null, visitedAt: todayStr });
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (existingReview) {
      setForm({
        rating: existingReview.rating || 0,
        tags: existingReview.tags || [],
        comment: existingReview.comment || '',
        difficulty: existingReview.difficulty || null,
        visitedAt: (existingReview.visitedAt || initialVisitedAt || todayStr).slice(0,10),
      });
      // If editing existing review with details, show optional section
      if (existingReview.comment || existingReview.tags?.length > 0 || existingReview.difficulty) {
        setShowOptionalDetails(true);
      }
    } else {
      setForm({ rating: 0, tags: [], comment: '', difficulty: null, visitedAt: (initialVisitedAt || todayStr) });
      setShowOptionalDetails(false);
    }
    setShowSuccess(false);
    setIsSubmitting(false);
    setError(null);
  }, [isOpen, existingReview, initialVisitedAt, todayStr]);

  const createMut = useMutation({
    mutationFn: (payload) => reviewAPI.create(projectId, payload),
    onSuccess: async () => {
      const invalidations = [
        qc.invalidateQueries(createQueryKey.reviewsSummary(projectId)),
        qc.invalidateQueries(createQueryKey.reviewsList(projectId, {})),
        qc.invalidateQueries(createQueryKey.myReview(projectId)),
        // Invalidate session reviews if this is a session-level review
        qc.invalidateQueries(['session-reviews', projectId]),
      ];
      // Also invalidate template queries if this is an instance review
      if (project?.originalProjectId && projectId !== project.originalProjectId) {
        invalidations.push(
          qc.invalidateQueries([`reviews-template-summary`, project.originalProjectId]),
          qc.invalidateQueries([`reviews-template-list`, project.originalProjectId])
        );
      }
      await Promise.all(invalidations);
    }
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }) => reviewAPI.update(id, payload),
    onSuccess: async () => {
      const invalidations = [
        qc.invalidateQueries(createQueryKey.reviewsSummary(projectId)),
        qc.invalidateQueries(createQueryKey.reviewsList(projectId, {})),
        qc.invalidateQueries(createQueryKey.myReview(projectId)),
        // Invalidate session reviews to update the Sessions tab
        qc.invalidateQueries(['session-reviews', projectId]),
      ];
      // Also invalidate template queries if this is an instance review
      if (project?.originalProjectId && projectId !== project.originalProjectId) {
        invalidations.push(
          qc.invalidateQueries([`reviews-template-summary`, project.originalProjectId]),
          qc.invalidateQueries([`reviews-template-list`, project.originalProjectId])
        );
      }
      await Promise.all(invalidations);
    }
  });

  const onSubmit = async () => {
    if (!form.rating) return;
    
    setIsSubmitting(true);
    try {
    const internalTags = Array.isArray(form.tags) ? [...form.tags] : [];
      // Tag with template ID if this is an instance review
      if (project?.originalProjectId && projectId !== project.originalProjectId) {
        const marker = `_template:${project.originalProjectId}`;
      if (!internalTags.includes(marker)) internalTags.push(marker);
    }

    const payload = {
      rating: form.rating,
      tags: internalTags,
      comment: form.comment || null,
      difficulty: form.difficulty || null,
      visitedAt: form.visitedAt,
      chatId: chatId || null, // Link review to specific session if provided
    };
      
    if (existingReview && existingReview.id) {
      await updateMut.mutateAsync({ id: existingReview.id, payload });
    } else {
      await createMut.mutateAsync(payload);
    }
      
      setShowSuccess(true);
      setTimeout(() => {
        onClose?.();
        setShowSuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to submit review:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

  // Animation variants for bottom slide (mobile) and spring bounce (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 30,
      opacity: 0,
      scale: 0.9,
      filter: 'blur(4px)',
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Spring animation for more lively, bouncy feel
  const transition = isMobile ? {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 0.8,
  } : {
    type: 'spring',
    stiffness: 400,
    damping: 25,
    mass: 0.6,
  };

  // Background transition (faster fade)
  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.25,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className={`fixed inset-0 z-[50003] backdrop-blur-md ${
            isMobile 
              ? 'flex items-end justify-center' 
              : 'flex items-center justify-center'
          }`}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={backgroundTransition}
          onClick={onClose}
        >
          <motion.div 
            className={`relative bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 w-full ${isMobile ? 'h-full rounded-none rounded-t-2xl' : 'max-w-xl rounded-2xl'} p-6 overflow-auto shadow-2xl`}
            role="dialog" 
            aria-modal="true" 
            style={{ 
              paddingTop: isMobile ? 'calc(var(--app-safe-area-top) + 1rem)' : undefined,
              touchAction: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05), 0 0 40px rgba(59, 130, 246, 0.1)'
            }}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
            onClick={(e) => e.stopPropagation()}
          >
        {/* Close */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {showSuccess ? 'Thanks for sharing!' : (sessionTitle ? 'Rate this session' : 'Review project')}
            </h3>
            {sessionTitle && !showSuccess && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{sessionTitle}</p>
            )}
          </div>
          <button onClick={onClose} className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-700 dark:text-gray-300">Close</button>
        </div>

        {showSuccess ? (
          <motion.div 
            className="py-16 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <motion.div 
                className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 15 }}
              >
                <motion.svg 
                  className="w-10 h-10 text-green-600 dark:text-green-400"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </motion.svg>
              </motion.div>
            </div>

            {/* Title */}
            <motion.h3 
              className="text-2xl font-semibold text-gray-900 dark:text-white mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              Thanks for sharing!
            </motion.h3>

            {/* Message */}
            <motion.p 
              className="text-gray-600 dark:text-gray-400 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              Your review helps the community discover great projects.
            </motion.p>

            {/* Show rating if provided */}
            {form.rating > 0 && (
              <motion.div 
                className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <span>You rated this</span>
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${i < form.rating ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.786 1.401 8.172L12 18.897l-7.335 3.872 1.401-8.172L.132 9.211l8.2-1.193z"/>
                    </svg>
                  ))}
              </div>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Required: Rating */}
            <div>
              <div className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                {sessionTitle ? 'How was this session?' : 'How was your project?'}
              </div>
              <Stars value={form.rating} size={isMobile ? 32 : 36} interactive onChange={(v) => setForm({ ...form, rating: v })} />
            </div>

            {/* Optional: Details Section (Collapsible) */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <button
                onClick={() => setShowOptionalDetails(!showOptionalDetails)}
                className="w-full flex items-center justify-between text-left mb-4 hover:opacity-80 transition-opacity"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {showOptionalDetails ? 'Hide' : 'Add'} optional details
                </span>
                <svg 
                  className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${showOptionalDetails ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {showOptionalDetails && (
                  <motion.div 
                    className="space-y-6"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                  {/* Tags */}
          <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-3">Tags</div>
            <div className="flex flex-wrap gap-2">
              {(tagsQuery.data || []).map((t, i) => {
                const active = form.tags.includes(t);
                return (
                          <button 
                            key={i} 
                            onClick={() => setForm({ ...form, tags: active ? form.tags.filter(x => x!==t) : [...form.tags, t] })} 
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              active 
                                ? 'bg-gray-900 dark:bg-white text-white dark:text-black border-gray-900 dark:border-white' 
                                : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600'
                            }`}
                          >
                            {t}
                          </button>
                );
              })}
            </div>
          </div>

                  {/* Comment */}
          <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-3">Tell others about the project</div>
            <textarea 
              value={form.comment} 
              onChange={(e) => setForm({ ...form, comment: e.target.value })} 
              placeholder="Consider including cool aspects of the project, facilities, or tips to come prepared." 
                      className="mobile-input w-full h-32 p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-300 resize-none"
              style={{ touchAction: 'manipulation', userSelect: 'text' }}
                    />
          </div>

                  {/* Difficulty */}
          <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-3">How difficult is this project?</div>
                    <div className="space-y-2">
            {['easy','moderate','hard','strenuous'].map((d) => (
                        <button 
                          key={d} 
                          className={`w-full text-left px-4 py-2.5 rounded-lg border transition-colors ${
                            form.difficulty === d 
                              ? 'border-gray-900 dark:border-white bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white' 
                              : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`} 
                          onClick={() => setForm({ ...form, difficulty: form.difficulty === d ? null : d })}
                        >
                          {d[0].toUpperCase()+d.slice(1)}
                        </button>
            ))}
                    </div>
                  </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Error Message */}
            {error && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                  {error}
            </div>
          </div>
        )}

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button 
                disabled={!form.rating || isSubmitting} 
                onClick={onSubmit}
                className={`w-full px-4 py-3 rounded-full font-medium transition-all ${
                  (!form.rating || isSubmitting) 
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' 
                    : 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/20'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        )}
          </motion.div>
        </motion.div>
        )}
    </AnimatePresence>
  );
}


